const ACCESS_TOKEN_LIFETIME_MINUTES = 30;
const REFRESH_TOKEN_LIFETIME_DAYS = 7;

const ACCESS_TOKEN_LIFETIME_MS = ACCESS_TOKEN_LIFETIME_MINUTES * 60 * 1000;
const REFRESH_TOKEN_LIFETIME_MS =  REFRESH_TOKEN_LIFETIME_DAYS * 24 * 60 * 60 * 1000;

class Prelogin extends HTMLElement {
    constructor() {
        super();
        this.form = this.querySelector('form');
        this.emailInput = this.form.querySelector('input[name="customer[email]"]');
    }

    async connectedCallback() {
        try {
            await this.checkAppStatus();

            // Make getTokenValidity available in the window object, so we can check online our token states.
            // Call window.getTokenValidity(true) to have full log.
            window.getTokenValidity  = this.getTokenValidity
            this.tokenRoutine();
            this.bindEvents();

            setInterval(() => {
                this.tokenRoutine()
            },  ACCESS_TOKEN_LIFETIME_MS);
        } catch (e) {
            console.log('Prelogin app unavailable :', e.toString());
            document.querySelector('.customer-login')?.classList.remove('customer-login--portal-enabled')
        }
    }

    /**
     * Check token validity of each tokens
     * @param verbose Boolean to display verbose log
     * @returns {boolean[]} An array of boolean, first index is for accessToken lifetime state, second index is for refresh token
     */
    getTokenValidity(verbose = false) {
        const tokensData = localStorage.getItem("accessTokenData");

        if(!tokensData) {
            return [false, false]
        }

        const tokens = JSON.parse(tokensData);

        const accessTokenCreatedAt = new Date(tokens.access_token_created_at);
        const refreshTokenCreatedAt = new Date(tokens.refresh_token_created_at);

        const accessTokenLifeTimeState = (accessTokenCreatedAt.getTime() + ACCESS_TOKEN_LIFETIME_MS) > Date.now();
        const refreshTokenLifeTimeState = (refreshTokenCreatedAt.getTime() + REFRESH_TOKEN_LIFETIME_MS) > Date.now();

        if(verbose) {
            console.log('🤔Check token validity:')
            console.table(tokens);
            console.table({
                'accessToken created at': new Date(accessTokenCreatedAt.getTime()),
                'refreshToken created at': new Date(refreshTokenCreatedAt.getTime()),
                'accessToken will expire at': new Date(accessTokenCreatedAt.getTime() + ACCESS_TOKEN_LIFETIME_MS),
                'now': new Date(),
            })
            console.log({accessTokenLifeTimeState, refreshTokenLifeTimeState})
        }

        return [
            accessTokenLifeTimeState,
            refreshTokenLifeTimeState
        ]
    }

    bindEvents() {
        if(typeof this.form === "undefined") {
            console.error("Prelogin form is not defined.")
            return null;
        }

        this.form.addEventListener("submit",  this.handleSubmit.bind(this))
    }

    /**
     * Check application status
     * @returns {Promise<void>}
     */
    async checkAppStatus() {
        const url = window.loginPortailAPI + "/v2/app/status";

        const response = await fetch(url, {
            headers: new Headers({
                'X-Shopify-Domain': Shopify.shop,
                'Content-Type': 'application/json'
            })
        });

        const body = await response.json();

        const appAvailable = response.status === 200 && body.status === "available";

        if(!appAvailable) {
            throw new Error('App unavailable');
        }
    }

    /**
     * Verify tokens and state and generate expired tokens if needed
     * @param verbose Boolean to display verbose log
     * @returns {Promise<undefined|{access_token, refresh_token: *, refresh_token_created_at: *, access_token_created_at: *}|{access_token, refresh_token, refresh_token_created_at: *, access_token_created_at: *}>}
     */
    async tokenRoutine(verbose = false) {

        const tokenValidity = this.getTokenValidity(verbose);

        // All tokens are expired
        if(tokenValidity.every(t => t === false)) {
            if(verbose) {
                console.log('Generate all tokens now...')
            }
            return await this.generateAllTokens();
        }

        // Only access token is expired
        if(tokenValidity[0] === false) {
            if(verbose) {
                console.log('Generate access token now...')
            }
            return await this.generateAccessTokenWithRefreshToken()
        }

        console.log('All token are still valid...')
    }

    /**
     * Generate all tokens (accessToken + refreshToken
     * @returns {Promise<{access_token, refresh_token, refresh_token_created_at: *, access_token_created_at: *}>}
     */
    async generateAllTokens() {
        const tokensPromise = await fetch(window.loginPortailAPI + "/access-token", {
            method: "post",
            headers: new Headers({
                "Content-Type": "application/json",
                "X-Shopify-Domain": Shopify.shop
            }),
        });

        const tokens = await tokensPromise.json();

        if (!tokens) {
            console.error("No access token returned from server.");
            return;
        }

        const accessTokenData = {
            access_token: tokens.accessToken,
            access_token_created_at: tokens.accessToken_created_at,
            refresh_token: tokens.refreshToken,
            refresh_token_created_at: tokens.refreshToken_created_at,
        };

        localStorage.setItem("accessTokenData", JSON.stringify(accessTokenData));

        return accessTokenData
    }

    /**
     * Generate an access token when the refresh token is provided
     * @returns {Promise<{access_token, refresh_token: *, refresh_token_created_at: *, access_token_created_at: *}>}
     */
    async generateAccessTokenWithRefreshToken() {
        const tokensData = JSON.parse(localStorage.getItem("accessTokenData"));

        if (!tokensData.refresh_token) {
            console.error("No refresh token available");
            return;
        }

        const updateAccessToken = await fetch(window.loginPortailAPI + "/access-token/new", {
            method: "POST",
            headers: new Headers({
                "Content-Type": "application/json",
                "X-Shopify-domain": Shopify.shop
            }),
            body: JSON.stringify({ refreshToken: tokensData.refresh_token })
        }).then(response => response.json())

        if (!updateAccessToken) {
            console.error("No data returned from server.");
            return;
        }

        const updatedAccessTokenData = {
            access_token: updateAccessToken.accessToken,
            access_token_created_at: updateAccessToken.accessToken_created_at,
            refresh_token: tokensData.refresh_token,
            refresh_token_created_at: tokensData.refresh_token_created_at,
        }

        localStorage.setItem("accessTokenData", JSON.stringify(updatedAccessTokenData));

        return updatedAccessTokenData
    }

    async handleSubmit(e) {
        e.preventDefault();
        const formData = new FormData(e.currentTarget);
        const email = formData.get("customer[email]");

        if (!this.validateEmail(email)) {
            this.emailError.classList.remove("u-none");
            return;
        }

        const accessTokenData = localStorage.getItem("accessTokenData");
        const token = JSON.parse(accessTokenData).access_token;
        const payload = { customer_email: email };

        const statusResponse = await fetch(window.loginPortailAPI + "/v2/customer/status", {
            method: e.currentTarget.method,
            headers: new Headers({
                "Authorization": "Bearer " + token,
                "Content-Type": "application/json",
                "X-Shopify-domain": Shopify.shop
            }),
            body: JSON.stringify(payload),
        })
            .then(response => response.json())
            .catch(error => {
                console.log(error);
            });

        return this.processResult(statusResponse);
    }

    async processResult(json) {
        if (!json) {
            console.error("No data returned from server.");
            return;
        }

        if (json.status === 0 || json.status === -1) {
            window.location.href = window.routes.account_register;
            return;
        }

        if (json.status === 1) {
            this.displayLoginView();
            return
        }

        if (json.status === 2 || json.status === 3) {
            const customer_id = json.customer_id;
            this.displayActiveAccountView(customer_id);
            return;
        }

        console.error("Unknown status code:", json.status);

/*
        if (!json.customer) {
            if(localStorage && json.email) {
                localStorage.setItem('re_e', btoa(json.email))
            }

            return window.lang == 'fr' ? window.location.href = `/account/register` : window.location.href = `/${window.lang}/account/register`;
        }*/
    }

    displayLoginView() {
        // go back and then come forward again immediately, to force :target to update
        history.pushState({}, "", "#login");
        history.back();
        let onpopstate = window.onpopstate;
        window.onpopstate = function() {
            history.forward();
            window.onpopstate = onpopstate;
        };

        // Fill login form with email
        const loginElement = document.querySelector('w3-login#login');
        if(loginElement && loginElement.fillEmailField) {
            loginElement.fillEmailField(this.emailInput.value)
        }

        if(loginElement && loginElement.clearPasswordField) {
            loginElement.clearPasswordField()
        }
    }

    validateEmail(string) {
        return String(string)
            .toLowerCase()
            .match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            );
    }

    displayActiveAccountView(customer_id) {
        history.pushState({}, "", "#send-invite");
        history.back();
        const onpopstate = window.onpopstate;
        window.onpopstate = function () {
            history.forward();
            window.onpopstate = onpopstate;
        };

        const activeAccountElement = document.querySelector('w3-send-invite');
        if (activeAccountElement && activeAccountElement.fillCustomerIdField) {
            activeAccountElement.fillCustomerIdField(customer_id);
        }
    }
}

customElements.define('w3-prelogin', Prelogin);
